import { Fragment, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Grid, Stack, Typography } from "@mui/material";
import BreadCrumbs from "components/breadcrumbs/Breadcrumbs";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Container from "@mui/material/Container";
import { FormFieldContainer } from "components/ui-elements";
import MarketingDashboardActions from "../../../redux-container/marketing-management/MarketingDashboardRedux";
import PageHeading from "../../../components/page-heading/PageHeading";
import { RouteConstants } from "routes/RouteConstants";
import styles from "./MarketingManagement.module.css";
import { TestimonialStatusTypeEnum } from "enumerations/StatusTypeEnum";
import { UpdatesStatusTypeEnum } from "enumerations/StatusTypeEnum";

var Highcharts = require("highcharts");
require("highcharts/modules/exporting")(Highcharts);

const actionDispatch = (dispatch: any) => {
  return {
    getMarketingDashboard: () =>
      dispatch(MarketingDashboardActions.getMarketingDashboardRequest()),
    setMarketingDashboardState: (key: any, data: any) =>
      dispatch(MarketingDashboardActions.setMarketingDashboardState(key, data)),
  };
};

const MarketingManagementDashboard = () => {
  const { getMarketingDashboard, setMarketingDashboardState } = actionDispatch(
    useDispatch()
  );

  const navigate = useNavigate();

  const marketingDashboard = useSelector(
    (state: any) => state?.marketingDashboard?.marketingDashboardData
  );
  const isGraphDataFetched = useSelector(
    (state: any) => state?.marketingDashboard?.isGraphDataFetched
  );

  const handleUpdateIcon = () => {
    navigate(RouteConstants.updateList, {
      state: { isNavigationFromSidemenu: true },
    });
  };

  const handleTestimonialIcon = () => {
    navigate(RouteConstants.testimonialsList, {
      state: { isNavigationFromSidemenu: true },
    });
  };
  const handleMediaAndPrIcon = () => {
    navigate(RouteConstants.mediaAndPrList, {
      state: { isNavigationFromSidemenu: true },
    });
  };

  const filterByStatus = (statusType: TestimonialStatusTypeEnum) => {
    const filterData = {
      statusType: statusType,
      isNavigationFromSidemenu: true,
    };
    navigate(`${RouteConstants.testimonialsList}`, {
      state: filterData,
    });
  };
  const filterByStatusMediaAndPrs = (statusType: TestimonialStatusTypeEnum) => {
    const filterData = {
      statusType: statusType,
      isNavigationFromSidemenu: true,
    };
    navigate(`${RouteConstants.mediaAndPrList}`, {
      state: filterData,
    });
  };

  const filterByUpdateStatus = (statusType: UpdatesStatusTypeEnum) => {
    const filterData = {
      statusType: statusType,
      isNavigationFromSidemenu: true,
    };
    navigate(`${RouteConstants.updateList}`, {
      state: filterData,
    });
  };

  const breadcrumbRoutes: any = [
    {
      name: "Marketing Management",
      route: RouteConstants.marketingManagementDashboard,
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  // useEffect(() => {
  //   loadData();
  //   configureMarketingChart();
  //   configureTestimonialsChart();
  // }, []);

  useEffect(() => {
    if (isGraphDataFetched) {
      setMarketingDashboardState("isGraphDataFetched", false);
      configureMarketingChart();
      configureTestimonialsChart();
      configureMediaAndPrsChart();
    }
  }, [isGraphDataFetched]);

  const loadData = () => {
    getMarketingDashboard();
  };

  const configureMarketingChart = () => {
    Highcharts.chart("updates-container", {
      chart: {
        backgroundColor: "#f7f6ff",
        plotBackgroundColor: "#f7f6ff",
        plotBorderWidth: 0,
        plotShadow: false,
      },
      title: {
        text: "",
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          slicedOffset: 0,
          dataLabels: {
            enabled: true,
            distance: -50,
            y: 0,
            style: {
              fontWeight: "bold",
              color: "black",
              fontSize: "14px",
            },
            filter: {
              property: "name",
              operator: "===",
              value: "Updates",
            },
          },
          borderWidth: 0,
        },
        series: {
          animation: true,
        },
      },
      series: [
        {
          type: "pie",
          name: "Total",
          innerSize: "80%",
          data: [
            {
              name: "Active Updates",
              y: marketingDashboard?.updatesOverview?.activeUpdatesCount,
              color: "#54b652",
              sliced: true,
            },
            {
              name: "Inactive Updates",
              y: marketingDashboard?.updatesOverview?.inactiveUpdatesCount,
              color: "#f37c7c",
              sliced: true,
            },
          ],
        },
      ],
    });
  };

  const configureTestimonialsChart = () => {
    Highcharts.chart("testimonials-container", {
      chart: {
        backgroundColor: "#f7f6ff",
        plotBackgroundColor: "#f7f6ff",
        plotBorderWidth: 0,
        plotShadow: false,
      },
      title: {
        text: "",
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          slicedOffset: 0,
          dataLabels: {
            enabled: true,
            distance: -50,
            y: 0,
            style: {
              fontWeight: "bold",
              color: "black",
              fontSize: "14px",
            },
            filter: {
              property: "name",
              operator: "===",
              value: "Testimonials",
            },
          },
          borderWidth: 0,
        },
        series: {
          animation: true,
        },
      },
      series: [
        {
          type: "pie",
          name: "Total",
          innerSize: "80%",
          data: [
            {
              name: "Active Testimonials",
              y: marketingDashboard?.testimonialsOverview
                ?.activeTestimonialsCount,
              color: "#54b652",
              sliced: true,
            },
            {
              name: "Inactive Testimonials",
              y: marketingDashboard?.testimonialsOverview
                ?.inactiveTestimonialCount,
              color: "#f37c7c",
              sliced: true,
            },
          ],
        },
      ],
    });
  };
  const configureMediaAndPrsChart = () => {
    Highcharts.chart("media-and-prs-container", {
      chart: {
        backgroundColor: "#f7f6ff",
        plotBackgroundColor: "#f7f6ff",
        plotBorderWidth: 0,
        plotShadow: false,
      },
      title: {
        text: "",
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          slicedOffset: 0,
          dataLabels: {
            enabled: true,
            distance: -50,
            y: 0,
            style: {
              fontWeight: "bold",
              color: "black",
              fontSize: "14px",
            },
            filter: {
              property: "name",
              operator: "===",
              value: "Testimonials",
            },
          },
          borderWidth: 0,
        },
        series: {
          animation: true,
        },
      },
      series: [
        {
          type: "pie",
          name: "Total",
          innerSize: "80%",
          data: [
            {
              name: "Active Media and Prs",
              y: marketingDashboard?.mediaAndPrsOverview
                ?.activeTestimonialsCount,
              color: "#54b652",
              sliced: true,
            },
            {
              name: "Inactive Media and Prs",
              y: marketingDashboard?.mediaAndPrsOverview
                ?.inactiveTestimonialCount,
              color: "#f37c7c",
              sliced: true,
            },
          ],
        },
      ],
    });
  };

  const navigateToUpdates = () => {
    navigate(RouteConstants.updateList, {
      state: { isNavigationFromSidemenu: true },
    });
  };

  const navigateToTestimonials = () => {
    navigate(RouteConstants.testimonialsList, {
      state: { isNavigationFromSidemenu: true },
    });
  };
  const navigateToMeidaAndprs = () => {
    navigate(RouteConstants.mediaAndPrList, {
      state: { isNavigationFromSidemenu: true },
    });
  };

  return (
    <Fragment>
      <PageHeading title="Marketing Management" />
      <BreadCrumbs routes={breadcrumbRoutes}></BreadCrumbs>

      <Container>
        <Grid className="heading-action-holder">
          <Typography className="heading">Dashboard</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={handleUpdateIcon}
              // autoFocus
              endIcon={<ChevronRightRoundedIcon />}
              disableElevation
              className="btn btn-dark"
            >
              Go To Updates
            </Button>

            <Button
              variant="contained"
              onClick={handleTestimonialIcon}
              // autoFocus
              endIcon={<ChevronRightRoundedIcon />}
              disableElevation
              className="btn btn-dark"
            >
              Go To Testimonials
            </Button>
            <Button
              variant="contained"
              onClick={handleMediaAndPrIcon}
              // autoFocus
              endIcon={<ChevronRightRoundedIcon />}
              disableElevation
              className="btn btn-dark"
            >
              Go To Media And Prs
            </Button>
          </Stack>
        </Grid>

        <Typography className="graph-container-heading"> Updates </Typography>
        <FormFieldContainer>
          <Grid className="graph-data-container">
            <Typography className="graph-heading">Updates Overview</Typography>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={4}
                md={4}
                lg={4}
                xl={4}
                className={styles["graph-container-holder"]}
              >
                <Grid id="updates-container" className="graph-holder" />
              </Grid>

              <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid
                    className={styles["total-stats-holder"]}
                    onClick={navigateToUpdates}
                  >
                    <span className="stat-card-total-value">
                      {marketingDashboard?.updatesOverview?.totalUpdatesCount}
                    </span>
                    <span className="stat-card-total-heading">
                      Total Published Updates
                    </span>
                    <span
                      className={styles["stat-card-total-icon"]}
                      onClick={handleUpdateIcon}
                    >
                      <ChevronRightRoundedIcon />
                    </span>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByUpdateStatus(UpdatesStatusTypeEnum.Inactive)
                      }
                    >
                      <span className="individual-stat-value-text red-stat-value">
                        {
                          marketingDashboard?.updatesOverview
                            ?.inactiveUpdatesCount
                        }
                      </span>
                      <span className="individual-stat-card-heading">
                        Inactive Updates
                      </span>
                      <span
                        className={styles["individual-stat-card-icon"]}
                        onClick={handleUpdateIcon}
                      >
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByUpdateStatus(UpdatesStatusTypeEnum.Published)
                      }
                    >
                      <span className="individual-stat-value-text green-stat-value">
                        {marketingDashboard?.updatesOverview.activeUpdatesCount}
                      </span>
                      <span className="individual-stat-card-heading">
                        Active Updates
                      </span>
                      <span
                        className={styles["individual-stat-card-icon"]}
                        onClick={handleUpdateIcon}
                      >
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FormFieldContainer>

        <Typography className="graph-container-heading margin-top-30px">
          Testimonials
        </Typography>
        <FormFieldContainer>
          <Grid className="graph-data-container">
            <Typography className="graph-heading">
              Testimonials Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={4}
                md={4}
                lg={4}
                xl={4}
                className={styles["graph-container-holder"]}
              >
                <Grid id="testimonials-container" className="graph-holder" />
              </Grid>

              <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid
                    className={styles["total-stats-holder"]}
                    onClick={navigateToTestimonials}
                  >
                    <span className="stat-card-total-value">
                      {
                        marketingDashboard?.testimonialsOverview
                          ?.totalTestimonialsCount
                      }
                    </span>
                    <span className="stat-card-total-heading">
                      Total Published Testimonials
                    </span>
                    <span
                      className={styles["stat-card-total-icon"]}
                      onClick={handleTestimonialIcon}
                    >
                      <ChevronRightRoundedIcon />
                    </span>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByStatus(TestimonialStatusTypeEnum.Inactive)
                      }
                    >
                      <span className="individual-stat-value-text red-stat-value">
                        {
                          marketingDashboard?.testimonialsOverview
                            ?.inactiveTestimonialCount
                        }
                      </span>
                      <span className="individual-stat-card-heading">
                        Inactive Testimonials
                      </span>
                      <span className={styles["individual-stat-card-icon"]}>
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByStatus(TestimonialStatusTypeEnum.Published)
                      }
                    >
                      <span className="individual-stat-value-text green-stat-value">
                        {
                          marketingDashboard?.testimonialsOverview
                            ?.activeTestimonialsCount
                        }
                      </span>
                      <span className="individual-stat-card-heading">
                        Active Testimonials
                      </span>
                      <span className={styles["individual-stat-card-icon"]}>
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FormFieldContainer>

        <Typography className="graph-container-heading margin-top-30px">
          Media And Prs
        </Typography>
        <FormFieldContainer>
          <Grid className="graph-data-container">
            <Typography className="graph-heading">
              Media And Prs Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={4}
                md={4}
                lg={4}
                xl={4}
                className={styles["graph-container-holder"]}
              >
                <Grid id="media-and-prs-container" className="graph-holder" />
              </Grid>

              <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid
                    className={styles["total-stats-holder"]}
                    onClick={navigateToMeidaAndprs}
                  >
                    <span className="stat-card-total-value">
                      {
                        marketingDashboard?.mediaAndPrsOverview
                          ?.totalTestimonialsCount
                      }
                    </span>
                    <span className="stat-card-total-heading">
                      Total Published Media And Prs
                    </span>
                    <span
                      className={styles["stat-card-total-icon"]}
                      onClick={handleMediaAndPrIcon}
                    >
                      <ChevronRightRoundedIcon />
                    </span>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByStatusMediaAndPrs(
                          TestimonialStatusTypeEnum.Inactive
                        )
                      }
                    >
                      <span className="individual-stat-value-text red-stat-value">
                        {
                          marketingDashboard?.mediaAndPrsOverview
                            ?.inactiveTestimonialCount
                        }
                      </span>
                      <span className="individual-stat-card-heading">
                        Inactive Media And Prs
                      </span>
                      <span className={styles["individual-stat-card-icon"]}>
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Grid
                      className={styles["individual-stats-holder"]}
                      onClick={() =>
                        filterByStatusMediaAndPrs(
                          TestimonialStatusTypeEnum.Published
                        )
                      }
                    >
                      <span className="individual-stat-value-text green-stat-value">
                        {
                          marketingDashboard?.mediaAndPrsOverview
                            ?.activeTestimonialsCount
                        }
                      </span>
                      <span className="individual-stat-card-heading">
                        Active Media And Prs
                      </span>
                      <span className={styles["individual-stat-card-icon"]}>
                        <ChevronRightRoundedIcon />
                      </span>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FormFieldContainer>
      </Container>
    </Fragment>
  );
};

export default MarketingManagementDashboard;
